import { useCallback, useEffect, useState } from "react";
import { Copy, Home, LogOut, RefreshCw, UserCheck } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { api, BACKEND_URL } from "@/lib/api";
import { useAuth } from "@/lib/auth";

const PAGE_SIZE = 50;

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
  } catch { return "—"; }
}
function fmtNum(n) {
  if (typeof n !== "number") return "—";
  return n.toLocaleString();
}

function StatCard({ label, value, sub, accent = "default" }) {
  const cls = accent === "dark"
    ? "bg-[#0a0a0a] text-white border-white/10"
    : "bg-white border-black/5";
  return (
    <div className={`rounded-[14px] border p-5 ${cls}`}>
      <div className="text-[10.5px] uppercase tracking-[0.12em] opacity-60">{label}</div>
      <div className="font-display tabular text-3xl font-extrabold mt-2.5 leading-none">{value}</div>
      {sub && <div className="text-[11px] opacity-60 mt-1.5">{sub}</div>}
    </div>
  );
}

function Pagination({ page, totalPages, total, onChange, loading }) {
  if (!total) return null;
  return (
    <div className="flex items-center justify-between gap-3 px-3 py-2 border-t border-black/5 text-[11.5px] text-neutral-600 bg-neutral-50">
      <div>
        Showing <span className="tabular font-medium">{(page - 1) * PAGE_SIZE + 1}</span>
        –<span className="tabular font-medium">{Math.min(page * PAGE_SIZE, total)}</span>
        {" "}of <span className="tabular font-medium">{total.toLocaleString()}</span>
        {loading && <span className="ml-2 text-neutral-400">refreshing…</span>}
      </div>
      <div className="flex items-center gap-1.5">
        <button type="button" onClick={() => onChange(1)} disabled={page <= 1 || loading} className="btn text-[11.5px] py-0.5 px-2 disabled:opacity-40">« First</button>
        <button type="button" onClick={() => onChange(Math.max(1, page - 1))} disabled={page <= 1 || loading} className="btn text-[11.5px] py-0.5 px-2 disabled:opacity-40">‹ Prev</button>
        <span className="px-2 tabular">Page {page} / {Math.max(1, totalPages)}</span>
        <button type="button" onClick={() => onChange(Math.min(totalPages || 1, page + 1))} disabled={page >= totalPages || loading} className="btn text-[11.5px] py-0.5 px-2 disabled:opacity-40">Next ›</button>
        <button type="button" onClick={() => onChange(totalPages || 1)} disabled={page >= totalPages || loading} className="btn text-[11.5px] py-0.5 px-2 disabled:opacity-40">Last »</button>
      </div>
    </div>
  );
}

export default function PartnerDashboard() {
  const navigate = useNavigate();
  const { user, setUser, logout } = useAuth();
  const [me, setMe] = useState(null);
  const [meErr, setMeErr] = useState("");
  const [users, setUsers] = useState([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(true);
  const [usersLoading, setUsersLoading] = useState(false);
  const [flash, setFlash] = useState("");
  const [impersonatingId, setImpersonatingId] = useState(null);
  const [copied, setCopied] = useState(false);

  const loadMe = useCallback(async () => {
    setLoading(true);
    setMeErr("");
    try {
      const { data } = await api.get("/partner/me");
      setMe(data);
    } catch (e) {
      setMeErr(
        e?.response?.status === 404
          ? "No partner is linked to this account yet — ask an admin to assign one."
          : e?.response?.data?.detail || e?.message || "load_failed",
      );
    } finally {
      setLoading(false);
    }
  }, []);

  const loadUsers = useCallback(async (p) => {
    setUsersLoading(true);
    try {
      const { data } = await api.get("/partner/users", { params: { page: p, page_size: PAGE_SIZE } });
      setUsers(data?.items || []);
      setTotal(data?.total || 0);
      setTotalPages(data?.total_pages || 0);
    } catch (e) {
      setMeErr(e?.response?.data?.detail || e?.message || "load_failed");
    } finally {
      setUsersLoading(false);
    }
  }, []);

  useEffect(() => { loadMe(); }, [loadMe]);
  useEffect(() => { loadUsers(page); }, [page, loadUsers]);

  function refresh() {
    loadMe();
    loadUsers(page);
  }

  async function copyLink() {
    if (!me?.partner?.slug) return;
    try {
      const url = `${window.location.origin}/r/${me.partner.slug}`;
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch { /* ignore */ }
  }

  async function impersonate(u) {
    setImpersonatingId(u.user_id);
    setFlash("");
    try {
      const { data } = await api.post(`/partner/users/${u.user_id}/impersonate`);
      if (data?.user) {
        setUser(data.user);
        navigate("/dashboard", { replace: true });
      }
    } catch (e) {
      setFlash(e?.response?.data?.detail || e?.message || "impersonate_failed");
    } finally {
      setImpersonatingId(null);
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <span className="spinner" />
      </div>
    );
  }

  if (meErr || !me?.partner) {
    return (
      <div className="min-h-screen bg-[var(--bg)] p-12">
        <div className="max-w-md mx-auto card p-6 text-center">
          <div className="font-display text-2xl font-extrabold">Partner dashboard</div>
          <p className="text-[13px] text-neutral-600 mt-3">{meErr || "No partner record linked."}</p>
          <div className="mt-4 flex items-center justify-center gap-2">
            <button onClick={refresh} className="btn btn-ghost text-[12.5px]"><RefreshCw size={13} /> Retry</button>
            <button onClick={logout} className="btn btn-primary text-[12.5px] inline-flex items-center gap-1.5">
              <LogOut size={13} /> Sign out
            </button>
          </div>
        </div>
      </div>
    );
  }

  const partner = me.partner;
  const funnel = me.funnel || {};
  const publicUrl = `${window.location.origin}/r/${partner.slug}`;

  return (
    <div className="min-h-screen bg-[var(--bg)]" data-testid="partner-dashboard">
      <div className="border-b border-black/5 bg-white">
        <div className="max-w-[1400px] mx-auto px-6 py-4 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            {partner.logo_url && (
              <img
                src={`${BACKEND_URL}${partner.logo_url}`}
                alt=""
                className="h-9 w-9 rounded-md border border-black/5 bg-white object-contain shrink-0"
              />
            )}
            <div className="min-w-0">
              <div className="font-display font-bold tracking-tight truncate">{partner.name}</div>
              <div className="text-[11.5px] text-neutral-500 truncate">Partner dashboard</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/dashboard" className="btn btn-ghost text-[12.5px] inline-flex items-center gap-1.5" data-testid="partner-dashboard-link">
              <Home size={13} /> Dashboard
            </Link>
            <Link to="/admin/deals" className="btn btn-ghost text-[12.5px]">Deals</Link>
            {user?.is_admin && (
              <Link to="/admin" className="btn btn-ghost text-[12.5px]">Admin home</Link>
            )}
            <button onClick={refresh} className="btn btn-ghost text-[12.5px]">
              <RefreshCw size={13} /> Refresh
            </button>
            <button onClick={logout} className="btn btn-ghost text-[12.5px] inline-flex items-center gap-1.5">
              <LogOut size={13} /> Sign out
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-[1400px] mx-auto px-6 py-7">
        <div className="section-eyebrow">Your link</div>
        <div className="mt-2 flex items-center gap-2 max-w-2xl">
          <code className="font-mono text-[13px] bg-white border border-black/10 rounded-md px-3 py-2 flex-1 truncate">
            {publicUrl}
          </code>
          <button onClick={copyLink} className="btn btn-primary text-[12.5px] inline-flex items-center gap-1.5">
            {copied ? "Copied" : <><Copy size={12} /> Copy</>}
          </button>
        </div>

        {flash && (
          <div className="mt-4 p-3 border rounded-[10px] text-[13px]" style={{ background: "var(--bad-bg)", color: "var(--bad)", borderColor: "var(--bad-border)" }}>
            {flash}
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-6">
          <StatCard
            label="Signups"
            value={fmtNum(funnel.signups || 0)}
            sub={`+${fmtNum(funnel.new_signups_7d || 0)} in the last 7 days`}
            accent="dark"
          />
          <StatCard
            label="Active users"
            value={fmtNum(funnel.active || 0)}
            sub="Have planned at least one trip"
          />
          <StatCard
            label="Last 7 days"
            value={fmtNum(funnel.new_signups_7d || 0)}
            sub="New signups via your link"
          />
        </div>

        <div className="mt-8 flex items-center justify-between">
          <h2 className="font-display text-lg font-bold tracking-tight">Referred users</h2>
        </div>
        <div className="card overflow-hidden mt-3" data-testid="partner-users-table">
          <table className="w-full text-[13px]">
            <thead className="text-left text-neutral-500 bg-neutral-50">
              <tr>
                <th className="py-2 px-3 font-medium">User</th>
                <th className="py-2 px-3 font-medium">Joined</th>
                <th className="py-2 px-3 font-medium text-right">Programs</th>
                <th className="py-2 px-3 font-medium text-right">Trips</th>
                <th className="py-2 px-3 font-medium">Last active</th>
                <th className="py-2 px-3 font-medium text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.length === 0 && !usersLoading && (
                <tr>
                  <td colSpan={6} className="py-8 text-center text-neutral-500 text-[13px]">
                    No signups yet. Share your link to start attributing visitors.
                  </td>
                </tr>
              )}
              {users.map((u) => (
                <tr key={u.user_id} className="border-t border-black/5" data-testid={`partner-user-row-${u.user_id}`}>
                  <td className="py-2 px-3 min-w-0">
                    <div className="font-semibold truncate">{u.name || u.email}</div>
                    <div className="text-[11.5px] text-neutral-500 truncate">{u.email}</div>
                  </td>
                  <td className="py-2 px-3 text-neutral-600 text-[12px] whitespace-nowrap">{fmtDate(u.created_at)}</td>
                  <td className="py-2 px-3 tabular text-right">{u.program_count ?? 0}</td>
                  <td className="py-2 px-3 tabular text-right">{u.trip_count ?? 0}</td>
                  <td className="py-2 px-3 text-neutral-600 text-[12px] whitespace-nowrap">{fmtDate(u.last_session_at)}</td>
                  <td className="py-2 px-3 text-right">
                    <button
                      type="button"
                      onClick={() => impersonate(u)}
                      disabled={impersonatingId !== null}
                      className="text-neutral-500 hover:text-black p-1.5 rounded transition-colors inline-flex items-center gap-1 text-[11.5px]"
                      data-testid={`partner-user-impersonate-${u.user_id}`}
                    >
                      {impersonatingId === u.user_id
                        ? <span className="spinner" />
                        : <><UserCheck size={13} /> Impersonate</>}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <Pagination page={page} totalPages={totalPages} total={total} onChange={setPage} loading={usersLoading} />
        </div>
      </div>
    </div>
  );
}
